// Kiinteistöni — Hallitse overlay: Kiinteistöt/Salkut tabs, master-detail inspector,
// CRUD (add/rename/delete building & salkku, edit salkku membership). Scope picking
// lives on the ScopeBar kind switch, not here.
import { useEffect, useMemo, useState } from "react";
import {
  X,
  Search,
  Building2,
  Layers,
  Briefcase,
  Plus,
  Trash2,
  Pencil,
  Check,
  Coins,
  Activity,
  AlertTriangle,
  ArrowRight,
  MapPin,
  ChevronDown,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { useBuildings } from "@/lib/api/useBuildings";
import {
  useInventoryFilter,
  type AssetType,
  type Status,
} from "@/lib/inventory-filter";
import { filterBuildings } from "@/lib/inventory-scope";
import {
  updateBuilding,
  deleteBuilding,
  listBuildingFindings,
  listGroups,
  createGroup,
  renameGroup,
  deleteGroup,
  addBuildingToGroup,
  removeBuildingFromGroup,
  type Building,
  type BuildingGroup,
  type BuildingStatus,
} from "@/lib/api/building";
import { addressFieldsPatch, parseCoordinateFields } from "@/lib/coordinates";
import { isOperator } from "@/lib/auth";
import { AddBuildingDialog } from "./AddBuildingDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  cardBase,
  cardSurface,
  cardSubtle,
  cardTonePill,
  cardToneText,
  inputGlowWrap,
  inputGlowInner,
} from "@/lib/card-styles";
import { cn } from "@/lib/utils";

const statusTone: Record<
  BuildingStatus,
  { dot: string; label: string; text: string }
> = {
  ok: { dot: "bg-success", label: "Optimoitu", text: "text-success" },
  warn: { dot: "bg-warning", label: "Potentiaalia", text: "text-warning" },
  alert: {
    dot: "bg-destructive",
    label: "Toimi nyt",
    text: "text-destructive",
  },
};

const typeOptions: { id: AssetType; label: string }[] = [
  { id: "all", label: "Kaikki tyypit" },
  { id: "Toimisto", label: "Toimisto" },
  { id: "Liiketila", label: "Liiketila" },
  { id: "Logistiikka", label: "Logistiikka" },
  { id: "Kampus", label: "Kampus" },
  { id: "Asuin", label: "Asuin" },
];
const statusOptions: { id: Status; label: string }[] = [
  { id: "all", label: "Kaikki tilat" },
  { id: "ok", label: "Optimoitu" },
  { id: "warn", label: "Potentiaalia" },
  { id: "alert", label: "Toimi nyt" },
];

const eur = (n: number) =>
  new Intl.NumberFormat("fi-FI", { maximumFractionDigits: 0 }).format(
    Math.round(n),
  );

function Select<T extends string>({
  icon: Icon,
  value,
  options,
  onChange,
}: {
  icon?: typeof MapPin;
  value: T;
  options: { id: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <label className="relative inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs hover:border-primary/40 transition">
      {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="bg-transparent outline-none pr-5 appearance-none text-foreground"
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="h-3 w-3 text-muted-foreground pointer-events-none absolute right-2" />
    </label>
  );
}

export function BuildingManagerOverlay({ onClose }: { onClose: () => void }) {
  const { refreshInventory } = useInventoryFilter();
  // Shared so Lisää kiinteistö refreshes both Kiinteistöt and Salkut member pickers.
  const [inventoryRefreshKey, setInventoryRefreshKey] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex flex-col">
      <header className="flex items-center justify-between border-b border-border px-4 sm:px-6 py-3.5 shrink-0">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-md bg-primary/10 grid place-items-center">
            <Building2 className="h-4 w-4 text-primary" />
          </span>
          <div>
            <div className="text-sm font-semibold tracking-tight">
              Kiinteistöni
            </div>
            <div className="text-[11px] text-muted-foreground">
              Hallitse kiinteistöjä ja salkkuja
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 grid place-items-center rounded-md border border-border bg-surface text-muted-foreground hover:text-foreground transition"
          title="Sulje (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      <Tabs
        defaultValue="buildings"
        className="flex-1 min-h-0 flex flex-col px-4 sm:px-6 py-4"
      >
        <TabsList className="self-start mb-3">
          <TabsTrigger value="buildings">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />
            Kiinteistöt
          </TabsTrigger>
          <TabsTrigger value="groups">
            <Layers className="h-3.5 w-3.5 mr-1.5" />
            Salkut
          </TabsTrigger>
        </TabsList>
        <TabsContent value="buildings" className="flex-1 min-h-0 mt-0">
          <BuildingsTab
            onSelect={onClose}
            refreshKey={inventoryRefreshKey}
            onInventoryChanged={() => {
              setInventoryRefreshKey((k) => k + 1);
              refreshInventory();
            }}
          />
        </TabsContent>
        <TabsContent value="groups" className="flex-1 min-h-0 mt-0">
          <GroupsTab buildingsRefreshKey={inventoryRefreshKey} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Kiinteistöt
// -----------------------------------------------------------------------------
function BuildingsTab({
  onSelect,
  refreshKey,
  onInventoryChanged,
}: {
  onSelect: () => void;
  refreshKey: number;
  onInventoryChanged: () => void;
}) {
  const { filter, setBuildingId, cityFilterOptions } = useInventoryFilter();
  const buildingsState = useBuildings({}, refreshKey);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({
    city: filter.city,
    type: filter.type,
    status: filter.status,
  });

  const buildings = useMemo(() => {
    if (buildingsState.status !== "ok") return [];
    const scoped = filterBuildings(buildingsState.buildings, draft, null);
    const q = query.trim().toLowerCase();
    if (!q) return scoped;
    return scoped.filter(
      (b) =>
        b.name.toLowerCase().includes(q) || b.city.toLowerCase().includes(q),
    );
  }, [buildingsState, draft, query]);

  const selected =
    buildingsState.status === "ok"
      ? (buildingsState.buildings.find((b) => b.id === selectedId) ?? null)
      : null;

  const searchBox = (
    <div className={inputGlowWrap}>
      <div
        className={cn("flex items-center gap-2 px-2.5 py-1.5", inputGlowInner)}
      >
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Hae nimellä tai paikkakunnalla…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
        />
      </div>
    </div>
  );

  const filterRow = (
    <div className="flex flex-wrap items-center gap-2 shrink-0">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground shrink-0">
        <Filter className="h-3 w-3" />
        Rajaa
      </span>
      <Select
        icon={MapPin}
        value={draft.city}
        options={cityFilterOptions}
        onChange={(v) => setDraft((d) => ({ ...d, city: v }))}
      />
      <Select
        icon={Building2}
        value={draft.type}
        options={typeOptions}
        onChange={(v) => setDraft((d) => ({ ...d, type: v }))}
      />
      <Select
        value={draft.status}
        options={statusOptions}
        onChange={(v) => setDraft((d) => ({ ...d, status: v }))}
      />
      <button
        onClick={() => setAddOpen(true)}
        className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-primary/15 transition"
      >
        <Plus className="h-3.5 w-3.5" />
        Lisää kiinteistö
      </button>
    </div>
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      {filterRow}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4 min-h-0">
        <div className={cn(cardBase, "flex flex-col min-h-0 p-3")}>
          <div className="mb-2">{searchBox}</div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
            {buildings.length === 0 && (
              <div className="px-2 py-6 text-center text-[11px] text-muted-foreground">
                Ei kohteita.
              </div>
            )}
            {buildings.map((b) => {
              const tone = statusTone[b.status];
              const active = b.id === selectedId;
              return (
                <button
                  key={b.id}
                  onClick={() => setSelectedId(b.id)}
                  className={cn(
                    "w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition",
                    active
                      ? "bg-primary/10 border border-primary/40"
                      : "border border-transparent hover:bg-surface",
                  )}
                >
                  <span
                    className={cn("h-2 w-2 rounded-full shrink-0", tone.dot)}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">
                      {b.name}
                    </span>
                    <span className="block text-[11px] text-muted-foreground truncate">
                      {b.city} · {tone.label}
                      {(b.lat == null || b.lon == null) && (
                        <>
                          {" · "}
                          <span className={cardToneText.warn}>
                            Ei koordinaatteja
                          </span>
                        </>
                      )}
                    </span>
                  </span>
                  {b.optimization_potential_eur_month != null && (
                    <span className="text-[11px] font-mono text-muted-foreground shrink-0">
                      {eur(b.optimization_potential_eur_month)} €/kk
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <BuildingInspector
          building={selected}
          onSelectScope={() => {
            if (selected) {
              setBuildingId(selected.id);
              onSelect();
            }
          }}
          onChanged={() => onInventoryChanged()}
          onDeleted={() => {
            setSelectedId(null);
            onInventoryChanged();
          }}
        />
      </div>
      <AddBuildingDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(building) => {
          onInventoryChanged();
          setSelectedId(building.id);
        }}
      />
    </div>
  );
}

function BuildingInspector({
  building,
  onSelectScope,
  onChanged,
  onDeleted,
}: {
  building: Building | null;
  onSelectScope: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [openFindings, setOpenFindings] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const operator = isOperator();

  // Kiinteistöni's BuildingInspector is the one editable home for address/city/coordinates
  // — AddBuildingDialog only creates. Lat/lon fields start empty on
  // every open (never pre-filled from the building's current coordinates): leaving them empty
  // means "let the backend geocode the new address" (or leave existing coordinates alone if the
  // address didn't change either) — pre-filling would silently re-submit the old coordinates and
  // block that re-geocode. See parseCoordinateFields.
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationDraft, setLocationDraft] = useState({
    address: "",
    city: "",
    lat: "",
    lon: "",
  });
  const [locationError, setLocationError] = useState<string | null>(null);

  useEffect(() => {
    if (!building) {
      setOpenFindings(null);
      return;
    }
    let cancelled = false;
    setOpenFindings(null);
    listBuildingFindings(building.id, { status: "open" })
      .then((f) => !cancelled && setOpenFindings(f.length))
      .catch(() => !cancelled && setOpenFindings(null));
    return () => {
      cancelled = true;
    };
  }, [building]);

  useEffect(() => {
    setEditingLocation(false);
    setLocationError(null);
  }, [building]);

  if (!building) {
    return (
      <div
        className={cn(
          cardBase,
          "flex items-center justify-center border-dashed min-h-0 p-6 text-sm text-muted-foreground",
        )}
      >
        Valitse kiinteistö vasemmalta tarkastellaksesi sen tilaa.
      </div>
    );
  }

  const tone = statusTone[building.status];

  async function handleRename() {
    if (!building) return;
    const name = window.prompt("Nimeä kohde uudelleen", building.name)?.trim();
    if (!name || name === building.name) return;
    setBusy(true);
    try {
      await updateBuilding(building.id, { name });
      onChanged();
    } catch {
      toast.error("Nimeäminen epäonnistui");
    } finally {
      setBusy(false);
    }
  }

  async function toggleLive() {
    if (!building) return;
    setBusy(true);
    try {
      await updateBuilding(building.id, {
        live_optimization_enabled: !building.live_optimization_enabled,
      });
      onChanged();
    } catch {
      toast.error("Live-optimoinnin vaihto epäonnistui");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!building) return;
    if (!confirm(`Poistetaanko kohde "${building.name}"?`)) return;
    setBusy(true);
    try {
      await deleteBuilding(building.id);
      toast.success("Kohde poistettu");
      onDeleted();
    } catch {
      toast.error("Poisto epäonnistui");
    } finally {
      setBusy(false);
    }
  }

  function openLocationEditor() {
    if (!building) return;
    setLocationDraft({
      address: building.address,
      city: building.city,
      lat: "",
      lon: "",
    });
    setLocationError(null);
    setEditingLocation(true);
  }

  async function handleSaveLocation() {
    if (!building) return;
    const address = locationDraft.address.trim();
    const city = locationDraft.city.trim();
    if (!address || !city) {
      setLocationError("Osoite ja kaupunki eivät voi olla tyhjiä.");
      return;
    }
    const parsedCoords = parseCoordinateFields(locationDraft);
    if (!parsedCoords.ok) {
      setLocationError(parsedCoords.error);
      return;
    }
    setLocationError(null);
    setBusy(true);
    try {
      await updateBuilding(building.id, {
        ...addressFieldsPatch(building, { address, city }),
        ...(parsedCoords.fields ?? {}),
      });
      setEditingLocation(false);
      onChanged();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Sijainnin päivitys epäonnistui",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cn(cardBase, "flex flex-col min-h-0 overflow-y-auto p-5")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold tracking-tight truncate">
            {building.name}
          </h2>
          <p className="text-xs text-muted-foreground truncate">
            {building.address} · {building.city}
          </p>
          {(building.lat == null || building.lon == null) && (
            <button
              type="button"
              onClick={openLocationEditor}
              className={cn(
                "mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px]",
                cardTonePill.warn,
              )}
              title="Kairos ei voinut määrittää sijaintia osoitteesta — muokkaa sijaintia korjataksesi"
            >
              <MapPin className="h-3 w-3" />
              Ei koordinaatteja
            </button>
          )}
        </div>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] shrink-0",
            tone.text,
          )}
        >
          <span className={cn("h-1.5 w-1.5 rounded-full", tone.dot)} />
          {tone.label}
        </span>
      </div>

      {editingLocation && (
        <div className={cn(cardSurface, "mt-3 p-3 space-y-2")}>
          <div className="grid grid-cols-2 gap-2">
            <LocationField label="Osoite">
              <Input
                value={locationDraft.address}
                onChange={(e) =>
                  setLocationDraft((d) => ({ ...d, address: e.target.value }))
                }
                disabled={busy}
              />
            </LocationField>
            <LocationField label="Kaupunki">
              <Input
                value={locationDraft.city}
                onChange={(e) =>
                  setLocationDraft((d) => ({ ...d, city: e.target.value }))
                }
                disabled={busy}
              />
            </LocationField>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <LocationField label="Leveysaste (valinnainen)">
              <Input
                value={locationDraft.lat}
                onChange={(e) =>
                  setLocationDraft((d) => ({ ...d, lat: e.target.value }))
                }
                placeholder={
                  building.lat != null
                    ? `Nykyinen: ${building.lat}`
                    : "esim. 60.1699"
                }
                inputMode="decimal"
                disabled={busy}
              />
            </LocationField>
            <LocationField label="Pituusaste (valinnainen)">
              <Input
                value={locationDraft.lon}
                onChange={(e) =>
                  setLocationDraft((d) => ({ ...d, lon: e.target.value }))
                }
                placeholder={
                  building.lon != null
                    ? `Nykyinen: ${building.lon}`
                    : "esim. 24.9384"
                }
                inputMode="decimal"
                disabled={busy}
              />
            </LocationField>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Jätä koordinaatit tyhjäksi: jos osoite muuttuu, Kairos hakee ne
            uudelleen automaattisesti; muuten nykyiset koordinaatit säilyvät.
          </p>
          {locationError && (
            <p className="text-[11px] text-destructive">{locationError}</p>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleSaveLocation}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              Tallenna
            </button>
            <button
              type="button"
              onClick={() => setEditingLocation(false)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground transition disabled:opacity-50"
            >
              Peruuta
            </button>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        <StatTile
          icon={Coins}
          label="Säästöpotentiaali"
          value={
            building.optimization_potential_eur_month != null
              ? `${eur(building.optimization_potential_eur_month)} €/kk`
              : "—"
          }
        />
        <StatTile
          icon={Activity}
          label="Aktiiviset optimoinnit"
          value={`${building.active_optimizations}`}
        />
        <StatTile
          icon={AlertTriangle}
          label="Avoimet havainnot"
          value={openFindings == null ? "…" : `${openFindings}`}
        />
        <div className={cn(cardSurface, "p-3")}>
          <div className="text-[11px] text-muted-foreground">
            Live-optimointi
          </div>
          <button
            onClick={toggleLive}
            disabled={!operator || busy}
            className={cn(
              "mt-1 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition disabled:opacity-50",
              building.live_optimization_enabled
                ? "bg-success/10 text-success"
                : "bg-surface text-muted-foreground",
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                building.live_optimization_enabled
                  ? "bg-success animate-pulse"
                  : "bg-muted-foreground",
              )}
            />
            {building.live_optimization_enabled ? "Päällä" : "Pois"}
          </button>
        </div>
      </div>

      <div className="mt-auto pt-5 flex flex-wrap items-center gap-2">
        <button
          onClick={onSelectScope}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition"
        >
          Valitse tarkasteluun
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
        {operator && (
          <button
            onClick={handleRename}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition disabled:opacity-50"
          >
            <Pencil className="h-3.5 w-3.5" />
            Nimeä
          </button>
        )}
        {operator && !editingLocation && (
          <button
            onClick={openLocationEditor}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition disabled:opacity-50"
          >
            <MapPin className="h-3.5 w-3.5" />
            Muokkaa sijaintia
          </button>
        )}
        {operator && (
          <button
            onClick={handleDelete}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:border-destructive/40 transition disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Poista
          </button>
        )}
      </div>
    </div>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Coins;
  label: string;
  value: string;
}) {
  return (
    <div className={cn(cardSurface, "p-3")}>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" />
        {label}
      </div>
      <div className="mt-1 text-base font-semibold">{value}</div>
    </div>
  );
}

function LocationField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

// -----------------------------------------------------------------------------
// Salkut
// -----------------------------------------------------------------------------
function GroupsTab({
  buildingsRefreshKey = 0,
}: {
  buildingsRefreshKey?: number;
}) {
  const [groups, setGroups] = useState<BuildingGroup[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const buildingsState = useBuildings({}, buildingsRefreshKey);
  const operator = isOperator();

  function refresh() {
    listGroups()
      .then(setGroups)
      .catch(() => toast.error("Salkkujen haku epäonnistui"));
  }
  useEffect(refresh, []);

  const selected = groups.find((g) => g.id === selectedId) ?? null;
  const allBuildings =
    buildingsState.status === "ok" ? buildingsState.buildings : [];

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const g = await createGroup(name);
      setGroups((gs) => [...gs, g]);
      setNewName("");
      setSelectedId(g.id);
    } catch {
      toast.error("Salkun luonti epäonnistui");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(g: BuildingGroup) {
    if (!confirm(`Poistetaanko salkku "${g.name}"?`)) return;
    try {
      await deleteGroup(g.id);
      setGroups((gs) => gs.filter((x) => x.id !== g.id));
      if (selectedId === g.id) setSelectedId(null);
    } catch {
      toast.error("Poisto epäonnistui");
    }
  }

  async function handleRename(g: BuildingGroup) {
    const name = window.prompt("Nimeä salkku uudelleen", g.name)?.trim();
    if (!name || name === g.name) return;
    try {
      const updated = await renameGroup(g.id, name);
      setGroups((gs) => gs.map((x) => (x.id === updated.id ? updated : x)));
    } catch {
      toast.error("Nimeäminen epäonnistui");
    }
  }

  async function toggleMember(buildingId: string, inGroup: boolean) {
    if (!selected) return;
    try {
      const updated = inGroup
        ? await removeBuildingFromGroup(selected.id, buildingId)
        : await addBuildingToGroup(selected.id, buildingId);
      setGroups((gs) => gs.map((g) => (g.id === updated.id ? updated : g)));
    } catch {
      toast.error("Salkun päivitys epäonnistui");
    }
  }

  return (
    <div className="h-full grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4 min-h-0">
      <div className={cn(cardBase, "flex flex-col min-h-0 p-3")}>
        {operator && (
          <div className="flex items-center gap-2 mb-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder="Uuden salkun nimi…"
              wrapperClassName="flex-1"
            />
            <button
              onClick={handleCreate}
              disabled={busy || !newName.trim()}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-2.5 py-2 text-xs font-medium text-primary hover:bg-primary/15 transition disabled:opacity-50 shrink-0"
            >
              <Plus className="h-3.5 w-3.5" />
              Luo
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
          {groups.length === 0 && (
            <div className="px-2 py-6 text-center text-[11px] text-muted-foreground">
              Ei salkkuja.
            </div>
          )}
          {groups.map((g) => {
            const active = g.id === selectedId;
            return (
              <button
                key={g.id}
                onClick={() => setSelectedId(g.id)}
                className={cn(
                  "w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition",
                  active
                    ? "bg-primary/10 border border-primary/40"
                    : "border border-transparent hover:bg-surface",
                )}
              >
                <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">
                    {g.name}
                  </span>
                  <span className="block text-[11px] text-muted-foreground">
                    {g.building_ids.length} kiinteistöä
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={cn(cardBase, "flex flex-col min-h-0 p-4")}>
        {!selected ? (
          <div className="flex-1 grid place-items-center text-sm text-muted-foreground">
            Valitse salkku hallitaksesi sen kiinteistöjä.
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold tracking-tight truncate">
                  {selected.name}
                </h2>
                <p className="text-[11px] text-muted-foreground">
                  {selected.building_ids.length} kiinteistöä
                </p>
              </div>
              {operator && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleRename(selected)}
                    className="h-7 w-7 grid place-items-center rounded-md border border-border bg-surface text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                    title="Nimeä salkku"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(selected)}
                    className="h-7 w-7 grid place-items-center rounded-md border border-border bg-surface text-muted-foreground hover:text-destructive hover:border-destructive/40 transition"
                    title="Poista salkku"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mb-1.5">
              Valitse mukaan tai poista salkusta:
            </p>
            <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1">
              {allBuildings.map((b) => {
                const inGroup = selected.building_ids.includes(b.id);
                return (
                  <button
                    key={b.id}
                    onClick={() => toggleMember(b.id, inGroup)}
                    disabled={!operator}
                    className={cn(
                      cardSubtle,
                      "w-full flex items-center gap-2.5 p-2.5 text-left transition disabled:opacity-60",
                      inGroup ? "border-primary/40" : "hover:border-primary/30",
                    )}
                  >
                    <span
                      className={cn(
                        "h-4 w-4 rounded grid place-items-center shrink-0 border",
                        inGroup
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border",
                      )}
                    >
                      {inGroup && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">
                        {b.name}
                      </span>
                      <span className="block text-[11px] text-muted-foreground truncate">
                        {b.city}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
